// =============================================================================
// domain/entitlement.ts — is this user entitled to Pro, and is the gate even on.
// -----------------------------------------------------------------------------
// Relocated from the old client's `PAID_LAUNCH` / `entitlementIsLive` /
// `isProUser` trio, which lives at app/index.html:39769 and :40098-40116.
//
// ── 🚨 WHY THIS EXISTS AT ALL ──
//
// The tab registry has carried `gate: 'pro'` on nine tabs since the shell was
// ported, and NOTHING READ IT. Every Pro screen rendered in full to everybody,
// and a comment two lines above the field claimed an upgrade panel that did not
// exist. It was invisible because the old client's own gate is switched off too
// (`PAID_LAUNCH = false`), so both clients agreed — a screenshot comparison and
// the differential harness are equally blind to a gate that nobody is holding.
//
// It stops being invisible the day paid signups open: the old client would gate
// and this one would not, in the client that actually launches.
//
// ── ⚠ THE SWITCH IS A PARAMETER, NOT A MODULE READ ──
//
// `hasProAccess` takes `paidLaunch` rather than reading the constant, so the
// LAUNCHED behaviour is testable today. A gate that can only be exercised by
// editing a constant is a gate nobody exercises, which is how the old client
// shipped a paywall whose panel had never been rendered in a test.
//
// ── 🚨 UNKNOWN FAILS OPEN, AND THE TWO DIRECTIONS ARE NOT SYMMETRIC ──
//
// Verbatim reasoning from the old client, because it is right and because the
// next reader will otherwise "tidy" it into a closed default:
//
//   a free user with Pro for one session is a bounded leak that corrects on the
//   next clean read, whereas a paying customer locked out of what they bought is
//   a support ticket, a refund request, and — because the billing card would
//   show them a buy button — possibly a duplicate subscription.
//
// ⚠ "Could not read" is NOT "has no subscription". The io layer must hand those
// back as different things; a null row means the read SUCCEEDED and found none.
//
// ── ⚠ THIS MIRRORS THE DATABASE'S OWN RULE, NOT THE OLD CLIENT'S JS ──
//
// `public.is_entitled(uuid)` (supabase/migrations/20260728000002) requires:
//   is_active AND revoked_at IS NULL AND (period_end IS NULL OR not >30d stale)
//
// The old client's JS omits `revoked_at` — it does not even fetch the column —
// and gets away with it because the Stripe webhook sets `is_active = false` in
// the same update that stamps `revoked_at`, and the client's query filters on
// `is_active`. Belt and braces cost nothing here, and the SQL function is the
// definition the server enforces, so this is the version worth holding.
// =============================================================================

/**
 * The paywall's master switch.
 *
 * 🔴 FALSE means EVERY GATE IN THE APP IS INERT — `hasProAccess` returns true
 * for everyone and every Pro tab renders in full. That is the correct state
 * during beta and it is what keeps this client at parity with the old one,
 * whose own switch is also false.
 *
 * ⚠ Flipping it is a LAUNCH action, not a code cleanup. The old client's launch
 * note is three lines in a frozen file (D53); this is the equivalent line here.
 */
export const PAID_LAUNCH = false;

/**
 * How long after a period ends a subscription still counts.
 *
 * ⚠ Not generosity — it is a webhook-outage buffer. A renewal that fails to
 * arrive must not lock out a paying customer within the hour.
 */
export const ENTITLEMENT_STALE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * The three fields that decide ACCESS, and nothing else.
 *
 * 🚨 THIS TYPE IS THE BOUNDARY, AND IT USED TO BE A COMMENT.
 *
 * The comment read: "the billing card will want tier, status and
 * `cancelAtPeriodEnd` when it is ported — they are deliberately absent here so
 * that a display concern cannot quietly become an access concern." The intent
 * was right. What happened is that the card WAS ported, the three fields were
 * never fetched, and the card printed the word "Pro" at everybody — including
 * Founders — and the word "Renews" over the date a cancelled subscription ENDS.
 *
 * ▶ So the fields exist now, and the separation is enforced by a signature
 * instead: `entitlementIsLive` takes THIS type, so it cannot read a display field
 * even by accident. Passing a full row still works — a wider object satisfies a
 * narrower parameter — but the rule's body can only see these three.
 *
 * ⚠ Adding a field here makes it part of who gets in. Adding one to
 * `EntitlementRow` below only makes it something the card can say.
 */
export interface EntitlementAccess {
  isActive: boolean;
  /** ISO timestamp, or null when the provider reported no period. */
  currentPeriodEnd: string | null;
  /** Set on refund or dispute, and sticky — a revocation never expires. */
  revokedAt: string | null;
}

/** What the plan is called. ⚠ Null on a row nobody's purchase code wrote. */
export type EntitlementTier = 'founder' | 'pro';

/**
 * One row of `public.billing_subscriptions`, in this app's spelling — the access
 * fields plus what the billing card needs to describe the subscription.
 */
export interface EntitlementRow extends EntitlementAccess {
  /**
   * ⚠ NULLABLE, and the fallback matters. The column is free text with no
   * constraint, and the "every live row has a tier" guarantee is upheld by one
   * line of the purchase webhook — it does not cover a seat granted by hand or
   * a support correction typed into the table. A null reads as the generic paid
   * name, never as a blank badge.
   */
  tier: EntitlementTier | null;
  /**
   * The provider's own word: active, trialing, past_due, unpaid, canceled,
   * paused, incomplete…
   *
   * 🔴 NEVER PRINT IT RAW. Two reasons: a customer whose card is failing should
   * not read "past_due", and a REFUNDED subscription can still carry the word
   * "active" — `revokedAt` is what settles that, not this.
   */
  status: string | null;
  /** They have cancelled; `currentPeriodEnd` is when it ENDS, not when it renews. */
  cancelAtPeriodEnd: boolean;
}

/**
 * What a read of the entitlement table produced.
 *
 * 🚨 Three outcomes, not two. `{ row: null }` means the read SUCCEEDED and this
 * user has no subscription — a real free user. `unknown` means the read FAILED
 * and we know nothing, which is the case that must fail open.
 */
export type EntitlementRead =
  | { unknown: false; row: EntitlementRow | null }
  | { unknown: true };

/** The read every screen starts from, before anything has come back. */
export const ENTITLEMENT_UNKNOWN: EntitlementRead = { unknown: true };

/**
 * Does this row grant access right now?
 *
 * Mirrors `public.is_entitled`. Takes `now` rather than calling the clock, so
 * the boundary is testable at 29 days and at 31.
 */
export function entitlementIsLive(row: EntitlementAccess | null, now: number): boolean {
  if (!row || !row.isActive) return false;
  // Sticky: a refunded or disputed subscription stays revoked even if a later
  // event flipped `is_active` back on.
  if (row.revokedAt) return false;
  // No period known — trust `is_active`.
  if (!row.currentPeriodEnd) return true;
  const end = Date.parse(row.currentPeriodEnd);
  // Unparseable — don't lock anyone out over a timestamp we failed to read.
  if (!Number.isFinite(end)) return true;
  return end > now - ENTITLEMENT_STALE_MS;
}

/**
 * The one question every gate asks.
 *
 * ⚠ Order matters and each line is load-bearing:
 *   1. the gate is switched off → everyone is Pro (this is beta, today)
 *   2. we could not read → everyone is Pro (see the header: fail open)
 *   3. otherwise the row decides
 */
export function hasProAccess(
  args: { paidLaunch: boolean; read: EntitlementRead; now: number },
): boolean {
  if (!args.paidLaunch) return true;
  if (args.read.unknown) return true;
  return entitlementIsLive(args.read.row, args.now);
}
